import axios from 'axios';

// Create axios instance with base URL and default headers
const apiClient = axios.create({
  baseURL: '/wp-json/rolex-bespoke-calendar/v1',
  headers: {
    'Content-Type': 'application/json',
    'X-WP-Nonce': window.rolexCalendarSettings ? window.rolexCalendarSettings.nonce : ''
  }
});

// Add request interceptor to handle CSRF token for WordPress
apiClient.interceptors.request.use(
  config => {
    // Add CSRF token from WordPress if available
    if (window.rolexCalendarSettings && window.rolexCalendarSettings.nonce) {
      config.headers['X-WP-Nonce'] = window.rolexCalendarSettings.nonce;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // Customize error object with additional info
    if (error.response) {
      // Server responded with an error status
      const customError = {
        ...error,
        status: error.response.status,
        statusText: error.response.statusText,
        isApiError: true
      };

      // Handle specific error codes
      if (error.response.status === 404) {
        // Se c'è un messaggio di errore dal server, usalo
        if (error.response.data && error.response.data.message) {
          // Controlla se è un appuntamento completato
          if (error.response.data.code === 'appointment_completed') {
            customError.message = 'Questo appuntamento è già passato e non può essere modificato.';
            customError.type = 'appointment_completed';
          } else {
            customError.message = 'La risorsa richiesta non è stata trovata';
            customError.type = 'not_found';
          }
        } else {
          customError.message = 'La risorsa richiesta non è stata trovata';
          customError.type = 'not_found';
        }
      }

      return Promise.reject(customError);
    } else if (error.request) {
      // Request was made but no response received
      const customError = {
        ...error,
        message: 'Nessuna risposta ricevuta dal server',
        isApiError: true,
        type: 'network_error'
      };
      return Promise.reject(customError);
    } else {
      // Something else happened while setting up the request
      const customError = {
        ...error,
        message: 'Si è verificato un errore durante la richiesta',
        isApiError: true,
        type: 'request_error'
      };
      return Promise.reject(customError);
    }
  }
);

// API service methods
export default {
  // Purpose endpoints
  getPurposes(locale) {
    return apiClient.get(`/purposes?locale=${locale}`);
  },

  // Store endpoints
  getStores(locale) {
    return apiClient.get(`/stores?locale=${locale}`);
  },

  // Availability endpoints
  getAvailableDates(storeId, purposeId, appointmentId = null) {
    const url = `/availability/dates?store_id=${storeId}&purpose_id=${purposeId}` +
      (appointmentId ? `&appointment_id=${appointmentId}` : '');

    return apiClient.get(url);
  },

  getAvailableTimes(storeId, purposeId, date, appointmentId = null) {
    const url = `/availability/times?store_id=${storeId}&purpose_id=${purposeId}&date=${date}` +
      (appointmentId ? `&appointment_id=${appointmentId}` : '');

    return apiClient.get(url);
  },

  // Appointment endpoints
  createAppointment(data) {
    return apiClient.post('/appointments', data);
  },

  getAppointment(id, token) {
    return apiClient.get(`/appointments/${id}?token=${token}`);
  },

  updateAppointment(id, token, data) {
    return apiClient.put(`/appointments/${id}?token=${token}`, data);
  },

  deleteAppointment(id, token) {
    return apiClient.delete(`/appointments/${id}`, {
      params: {
        token
      }
    });
  },

  // Notification preferences endpoints
  setEarlierNotificationPreference(appointmentId, enableNotification) {
    return apiClient.post('/appointments/notification-preference', {
      appointment_id: appointmentId,
      notify_earlier: enableNotification
    });
  },

  // Admin endpoints
  getAppointmentsByDateRange(startDate, endDate) {
    return apiClient.get(`/admin/appointments?start_date=${startDate}&end_date=${endDate}`);
  }
};